import { Component, OnDestroy, OnInit } from '@angular/core';
import {
	UntypedFormBuilder,
	UntypedFormGroup,
	Validators,
} from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';

import { Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { Store } from '@ngrx/store';

import {
	DashReportType,
	OrgListSummary,
} from '../../models/organizations.model';
import {
	fetchOrgsSummaryListStart,
	restoreFormReset,
	saveDashOrgURLStart,
} from '../../state/organizations.actions';
import {
	selectDashChartUrl,
	selectOrgList,
	selectOrgLoading,
	selectRestoreForm,
} from '../../state/organizations.selector';

@Component({
	selector: 'app-org-reports-link',
	styleUrls: ['./org-reports-link.component.scss'],
	templateUrl: './org-reports-link.component.html',
})
export class OrgReportsLinkComponent implements OnInit, OnDestroy {
	editing: boolean = false;
	form: UntypedFormGroup;
	inputId = `${Math.random()}`;

	isWorking: boolean = false;
	linkId?: string;
	loading$: Observable<boolean>;
	orgId: string = '';
	orgOpts: { id: string; name: string }[] = [];
	orgs$: Observable<OrgListSummary[]>;
	typeOpts: { id: string; name: string }[] = [
		{ id: 'HEALTH', name: 'Saúde' },
		{ id: 'SPECIALITY', name: 'Especialidades' },
		{ id: 'REASON', name: 'Motivos' },
	];

	private readonly unsubscribe$: Subject<void> = new Subject();

	constructor(
		private readonly activatedRoute: ActivatedRoute,
		private readonly fb: UntypedFormBuilder,
		private readonly router: Router,
		private readonly store: Store,
	) {
		this.form = this.fb.group({
			link: [null, Validators.required],
			name: [null, Validators.required],
			organization_id: [{ disabled: true, value: null }, Validators.required],
			organization_name: [null, Validators.required],
		});
		this.loading$ = this.store.select(selectOrgLoading);
		this.orgs$ = this.store.select(selectOrgList);
	}

	ngOnDestroy(): void {
		this.unsubscribe$.next();
		this.unsubscribe$.complete();
		this.store.dispatch(restoreFormReset());
	}

	ngOnInit(): void {
		this.activatedRoute.params.subscribe(({ id, lId }) => {
			this.orgId = id;
			if (lId) {
				this.store
					.select(selectDashChartUrl)
					.pipe(takeUntil(this.unsubscribe$))
					.subscribe((l) => {
						if (!l) {
							this.router.navigate(['organizacoes', this.orgId, 'painel']);
						} else {
							this.form = this.fb.group({
								link: [l.link, Validators.required],
								name: [l.name, Validators.required],
								organization_id: [l.organization_id, Validators.required],
								organization_name: [l.organization_name, Validators.required],
							});
							this.linkId = l.id;
							this.form.controls.organization_name.disable();
							this.editing = true;
						}
					});
			}
		});
		this.store.dispatch(fetchOrgsSummaryListStart({ id: this.orgId }));
		this.store
			.select(selectOrgList)
			.pipe(takeUntil(this.unsubscribe$))
			.subscribe((ol) => {
				if (ol.length) {
					this.orgOpts = ol.map((o) => ({ id: o.id, name: o.name }));
					this.orgOpts.sort((a, b) => (a.name > b.name ? 1 : -1));
					this.form.controls.organization_id.enable();
				}
			});
	}

	onOrgChange(org: string): void {
		const organization_name = this.orgOpts.find((o) => o.id === org)?.name;
		this.form.patchValue({ organization_name });
	}

	onSaveLink(): void {
		if (this.form.valid) {
			this.isWorking = true;
			const request = this.form.value;
			this.store.dispatch(
				saveDashOrgURLStart({
					linkId: this.linkId,
					orgId: this.orgId,
					request,
				}),
			);
			this.store
				.select(selectRestoreForm)
				.pipe(takeUntil(this.unsubscribe$))
				.subscribe((restore) => {
					if (restore) {
						this.isWorking = false;
					}
				});
		}
	}

	processChartType(t: DashReportType): string {
		switch (t) {
			case 'HEALTH':
				return 'Saúde';
			case 'SPECIALITY':
				return 'Especialidades';
			case 'REASON':
				return 'Motivos';
			default:
				return '';
		}
	}
}
